
'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeatureAccessPanel from './FeatureAccessPanel';
import { TermsConditions } from './terms_conditions/TermsConditions';
import TermsTextEditor from './terms_conditions/TermsTextEditor';
import { AddApiKeysAdmin } from './multiple_api_keys/AddApiKeysAdmin';
import { SpeechToTextOptions } from './speech_and_llm_option/SpeechToTextOptions';
import { LlmOptionsSettings } from './speech_and_llm_option/LlmOptionSettings';
import UseToast from './UseToast';
import { AuthContext } from '../AuthContext';
import { useContext } from 'react';
import { MultipleApiKeysSettings } from './multiple_api_keys/MultipleApiKeysSettings';
import LoginCustomizationPanel from './backend/LoginCustomizationPanel';
import LoginSlugManager from './backend/LoginSlugManager';
import { AddApiKeysTab } from './multiple_api_keys/AddApiKeysTab';
import Select from 'react-select';
import axios from 'axios';
import MotiveIntegrationForm from './multiple_api_keys/MotiveIntegrationForm';
import { MotiveIntegrationTab } from './multiple_api_keys/MotiveIntegrationTab';
import { ChatGptModelIntegration } from './multiple_api_keys/ChatGptModelIntegration';
import ChatGptIntegration from './multiple_api_keys/ChatGptIntegration';
import { Grid } from '@mui/material';
import { CaptionFileSettings } from './CaptionFileSettings';
import { FaUsers } from 'react-icons/fa';
import { IoKeySharp } from "react-icons/io5";
import { FaRobot } from "react-icons/fa";
import { FaLanguage } from 'react-icons/fa6';
import { CiStreamOn } from "react-icons/ci";
import { WiDaySunny } from 'react-icons/wi';


// Comprehensive list of languages with locale codes
const AVAILABLE_LANGUAGES = [
  { code: 'en-US', name: 'English (United States)', flag: 'US' },
  { code: 'en-GB', name: 'English (United Kingdom)', flag: 'GB' },
  { code: 'es-ES', name: 'Spanish (Spain)', flag: 'ES' },
  { code: 'es-MX', name: 'Spanish (Mexico)', flag: 'MX' },
  { code: 'fr-FR', name: 'French (France)', flag: 'FR' },
  { code: 'fr-CA', name: 'French (Canada)', flag: 'CA' },
  { code: 'de-DE', name: 'German (Germany)', flag: 'DE' },
  { code: 'it-IT', name: 'Italian (Italy)', flag: 'IT' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', flag: 'BR' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', flag: 'PT' },
  { code: 'ru-RU', name: 'Russian (Russia)', flag: 'RU' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', flag: 'CN' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', flag: 'TW' },
  { code: 'ja-JP', name: 'Japanese (Japan)', flag: 'JP' },
  { code: 'ko-KR', name: 'Korean (South Korea)', flag: 'KR' },
  { code: 'hi-IN', name: 'Hindi (India)', flag: 'IN' },
  { code: 'ar-SA', name: 'Arabic (Saudi Arabia)', flag: 'SA' },
  { code: 'tr-TR', name: 'Turkish (Turkey)', flag: 'TR' },
  { code: 'pl-PL', name: 'Polish (Poland)', flag: 'PL' },
  { code: 'nl-NL', name: 'Dutch (Netherlands)', flag: 'NL' },
  { code: 'sv-SE', name: 'Swedish (Sweden)', flag: 'SE' },
  { code: 'da-DK', name: 'Danish (Denmark)', flag: 'DK' },
  { code: 'no-NO', name: 'Norwegian (Norway)', flag: 'NO' },
  { code: 'fi-FI', name: 'Finnish (Finland)', flag: 'FI' },
  { code: 'cs-CZ', name: 'Czech (Czech Republic)', flag: 'CZ' },
  { code: 'hu-HU', name: 'Hungarian (Hungary)', flag: 'HU' },
  { code: 'ro-RO', name: 'Romanian (Romania)', flag: 'RO' },
  { code: 'bg-BG', name: 'Bulgarian (Bulgaria)', flag: 'BG' },
  { code: 'hr-HR', name: 'Croatian (Croatia)', flag: 'HR' },
  { code: 'sk-SK', name: 'Slovak (Slovakia)', flag: 'SK' },
  { code: 'sl-SI', name: 'Slovenian (Slovenia)', flag: 'SI' },
  { code: 'et-EE', name: 'Estonian (Estonia)', flag: 'EE' },
  { code: 'lv-LV', name: 'Latvian (Latvia)', flag: 'LV' },
  { code: 'lt-LT', name: 'Lithuanian (Lithuania)', flag: 'LT' },
  { code: 'uk-UA', name: 'Ukrainian (Ukraine)', flag: 'UA' },
  { code: 'he-IL', name: 'Hebrew (Israel)', flag: 'IL' },
  { code: 'th-TH', name: 'Thai (Thailand)', flag: 'TH' },
  { code: 'vi-VN', name: 'Vietnamese (Vietnam)', flag: 'VN' },
  { code: 'id-ID', name: 'Indonesian (Indonesia)', flag: 'ID' },
  { code: 'ms-MY', name: 'Malay (Malaysia)', flag: 'MY' },
  { code: 'tl-PH', name: 'Filipino (Philippines)', flag: 'PH' },
];

// Reusable Flag Component using flag-icons
export const Flag = ({ code, size = '1.5em', squared = false }) => {
  const flagClass = `fi fi-${code.toLowerCase()} ${squared ? 'fis' : ''}`;

  return (
    <span
      className={flagClass}
      style={{
        fontSize: size, // This controls the flag size
        margin: '5px 10px',
      }}
    />
  );
};

const GlobalSettings = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [selectedLanguages, setSelectedLanguages] = useState(['en-US', 'es-ES', 'fr-FR']);
  const [languageSearch, setLanguageSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientSettings, setClientSettings] = useState({
    // Feature access settings
    featureAccess: {
      aihEnabled: true,
      menuQuizEnabled: true,
      wayfinderEnabled: false,
    },
    // System preferences
    systemPreferences: {
      defaultSystem: 'all',
    },
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  ////////////////---------For multiple Api Keys---------------//////////////////////
  const [formData, setFormData] = useState({
    heygen_api_keys: [{ name: '', api_key: '', enableHighQuality: false }],
    avatar_api_keys: [{ name: '', api_key: '' }],
    voice_api_keys: [{ name: '', api_key: '' }],
    open_api_keys: [{ name: '', api_key: '', model: '' }],
    weather_api_keys: [{ name: '', api_key: '' }],
  });

  const [docExists, setDocExists] = useState(false);
  // State
  const [initialSpeech, setInitialSpeech] = useState(null);
  const [initialLlm, setInitialLlm] = useState(null);

  const id = selectedClient;
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  const { authState } = useContext(AuthContext);
  const token = authState?.token;
  //////////////////////////--------------------------------------//////////////////////

  const [assignedStreamingConfig, setAssignedStreamingConfig] = useState({
    monthlyOpenAiTotalToken: '',
    liveStreamingMint: '',
  });

  //////////////------------------For User Api Keys-----------//////////////////////
  const [availableKeys, setAvailableKeys] = useState({
    heygen: [],
    avatar: [],
    voice: [],
    openai: [],
    weather: [],
  });
  const [uploadingFiles, setUploadingFiles] = useState({});

  const [userAssignedKeys, setUserAssignedKeys] = useState(null);

  const [selectedKeys, setSelectedKeys] = useState({
    heygen: [],
    avatar: [],
    voice: [],
    openai: [],
    weather: [],
  });

  const [selectedConfig, setSelectedConfig] = useState(null);

  // Add state for motive integration

  const [motiveConfigs, setMotiveConfigs] = useState([]);

  const [motiveLoading, setMotiveLoading] = useState(false);

  const [motiveHasChanges, setMotiveHasChanges] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [captionSettings, setCaptionSettings] = useState({
    status: false,
  });

  // Format options for react-select

  const formatOptions = keys => {
    if (!Array.isArray(keys)) return [];

    return keys.map(key => ({
      value: key._id, // Use _id as value for selection

      label: key.name,
    }));
  };

  // Get currently selected options for display

  const getSelectedOptions = keyType => {
    const available = availableKeys[keyType] || [];

    return available.filter(key => selectedKeys[keyType].includes(key._id));
  };

  // Handle key selection change

  const handleKeySelectionChange = (keyType, selectedOptions) => {
    setSelectedKeys(prev => ({
      ...prev,

      [keyType]: selectedOptions.map(option => option.value),
    }));
  };

  const saveAssignedKeys = async () => {
    if (!selectedClient) {
      alert('Please select a client first');

      return;
    }

    console.log('🔑 Sending API key assignment:', {
      userId: selectedClient,

      selectedKeys,
    });

    setLoading(true);

    try {
      const res = await fetch(
        `/api/user-api-keys/assign-keys/${selectedClient}`,

        {
          method: 'POST',

          headers: {
            'Content-Type': 'application/json',
          },

          body: JSON.stringify({
            heygen_key_ids: selectedKeys.heygen,

            avatar_key_ids: selectedKeys.avatar,

            voice_key_ids: selectedKeys.voice,

            openai_key_ids: selectedKeys.openai,

            weather_key_ids: selectedKeys.weather,
          }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        showMsgSuccessToast('API keys assigned successfully!');

        fetchUserAssignedKeys(); // Refresh assigned keys
      } else {
        showErrorToast(data.message || 'Failed to assign API keys');
      }
    } catch (error) {
      console.error('Error assigning API keys:', error);

      showErrorToast('Failed to assign API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableKeys = async () => {
    try {
      const res = await fetch('/api/user-api-keys/available-keys');

      const data = await res.json();

      if (res.ok) {
        setAvailableKeys(data);
      }
    } catch (error) {
      console.error('Error fetching available keys:', error);
    }
  };

  // In the main component, update the file upload section:
  const handleFileUpload = async (fieldName, index, fileType, file) => {
    const uploadKey = `${fieldName}-${index}-${fileType}`;

    try {
      // Validate file
      if (!file) {
        showErrorToast('No file selected');
        return;
      }

      // Validate file type
      const fileTypeValidation = {
        avatarImage: /^image\//,
        avatarLoopVideo: /^video\//,
        voiceKeyAudioSample: /^audio\//,
      };

      if (fileTypeValidation[fileType] && !fileTypeValidation[fileType].test(file.type)) {
        const expectedType = fileType.includes('Image') ? 'image' : fileType.includes('Video') ? 'video' : 'audio';
        showErrorToast(`Please select a valid ${expectedType} file`);
        return;
      }

      // Validate file size (10MB for images/audio, 50MB for videos)
      const maxFileSize = fileType === 'avatarLoopVideo' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxFileSize) {
        const sizeLimit = fileType === 'avatarLoopVideo' ? '50MB' : '10MB';
        showErrorToast(`File size exceeds ${sizeLimit}. Please upload a smaller file.`);
        return;
      }

      setUploadingFiles(prev => ({
        ...prev,
        [uploadKey]: true,
      }));

      // Generate signed URL for S3 upload
      const response = await axios.get('/generate-signed-url', {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      if (!response.data || !response.data.url) {
        throw new Error('Failed to generate upload URL');
      }

      const { url } = response.data;

      // Upload the File to S3
      await axios.put(url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Get the public URL
      const fileUrl = url.split('?')[0];

      // Update the form data with the S3 URL AND original filename
      handleArrayChange(fieldName, index, fileType, fileUrl);
      handleArrayChange(fieldName, index, `${fileType}FileName`, file.name);

      showMsgSuccessToast(
        `${fileType === 'avatarLoopVideo' ? 'Video' : 'File'} uploaded successfully`
      );
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error uploading file';
      showErrorToast(errorMessage);
    } finally {
      setUploadingFiles(prev => ({
        ...prev,
        [uploadKey]: false,
      }));
    }
  };

  useEffect(() => {
    fetchAvailableKeys();
  }, []);

  const fetchUserAssignedKeys = async () => {
    if (!selectedClient) return;

    try {
      const res = await fetch(`/api/user-api-keys/user-assigned-keys/${selectedClient}`);

      const data = await res.json();

      if (res.ok) {
        setUserAssignedKeys(data.user);

        // Extract key_ids from assigned key objects

        setSelectedKeys({
          heygen: data.user.assignedKeys.heygen.map(k => k.key_id || k._id),

          avatar: data.user.assignedKeys.avatar.map(k => k.key_id || k._id),

          voice: data.user.assignedKeys.voice.map(k => k.key_id || k._id),

          openai: data.user.assignedKeys.openai.map(k => k.key_id || k._id),

          weather: (data.user.assignedKeys.weather || []).map(k => k.key_id || k._id),
        });
      }
    } catch (error) {
      console.error('Error fetching user assigned keys:', error);
    }
  };

  useEffect(() => {
    fetchUserAssignedKeys();
  }, [selectedClient]);

  //////////////////////////////--------------------//////////////////////////////////////

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
    fetchLanguageSettings();
  }, []);

  // Fetch client settings when client is selected
  useEffect(() => {
    if (selectedClient) {
      fetchClientSettings(selectedClient);
    }
  }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const response = await fetch('/auth/clients');
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setMessage('Error fetching clients');
    }
  };

  const fetchLanguageSettings = async () => {
    try {
      const response = await fetch('/api/settings/languages');
      const data = await response.json();
      if (data.languages) {
        setSelectedLanguages(data.languages);
      }
    } catch (error) {
      console.error('Error fetching language settings:', error);
    }
  };

  const fetchClientSettings = async clientId => {
    try {
      const response = await fetch(`/auth/settings/client/${clientId}`);
      const data = await response.json();
      if (data.settings) {
        setClientSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching client settings:', error);
    }
  };

  const filteredLanguages = AVAILABLE_LANGUAGES.filter(
    lang =>
      lang.name.toLowerCase().includes(languageSearch.toLowerCase()) ||
      lang.code.toLowerCase().includes(languageSearch.toLowerCase())
  );

  const handleLanguageToggle = languageCode => {
    setSelectedLanguages(prev =>
      prev.includes(languageCode)
        ? prev.filter(code => code !== languageCode)
        : [...prev, languageCode]
    );
  };

  const removeLanguage = languageCode => {
    setSelectedLanguages(prev => prev.filter(code => code !== languageCode));
  };

  const handleFeatureToggle = feature => {
    setClientSettings(prev => ({
      ...prev,
      allowedFeatures: prev.allowedFeatures.includes(feature)
        ? prev.allowedFeatures.filter(f => f !== feature)
        : [...prev.allowedFeatures, feature],
    }));
  };

  const saveLanguageSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/settings/languages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ languages: selectedLanguages }),
      });

      if (response.ok) {
        setMessage('Language settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error saving language settings');
      }
    } catch (error) {
      console.error('Error saving language settings:', error);
      setMessage('Error saving language settings');
    }
    setLoading(false);
  };

  const saveClientSettings = async () => {
    if (!selectedClient) {
      setMessage('Please select a client first');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/auth/settings/client/${selectedClient}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: clientSettings }),
      });

      if (response.ok) {
        showMsgSuccessToast('Client settings saved successfully!');
        setMessage('Client settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error saving client settings');
      }
    } catch (error) {
      showErrorToast('Error saving client settings');
      console.error('Error saving client settings:', error);
      setMessage('Error saving client settings');
    }
    setLoading(false);
  };

  const getSelectedClientInfo = () => {
    const client = clients.find(c => c._id === selectedClient);
    if (!client) return null;

    return {
      // first_name: client.first_name || "",
      // last_name: client.last_name || "",
      company_name: client?.company_name || '',
      email: client.email,
    };
  };

  const selectedClientInfo = getSelectedClientInfo();

  ////////////////////////////////// 24 September ///////////////////////////////////////////////////////////////////////////////
  //////////////////////////////------------Multiple Api Keys ----------------------/////////////////////////////////////////////

  /** ---------- Helpers ---------- */
  const ensureApiArrays = user => {
    const apiFields = ['heygen_api_keys', 'avatar_api_keys', 'voice_api_keys', 'open_api_keys', 'weather_api_keys'];

    const out = { ...user };

    apiFields.forEach(f => {
      if (!Array.isArray(out[f]) || out[f].length === 0) {
        // Initialize with appropriate fields based on key type
        if (f === 'avatar_api_keys') {
          out[f] = [
            {
              name: '',
              api_key: '',
              avatarImage: '',
              avatarImageFileName: '',
              avatarLoopVideo: '',
              avatarLoopVideoFileName: '',
              originalFileName: '',
            },
          ];
        } else if (f === 'voice_api_keys') {
          out[f] = [
            {
              name: '',
              api_key: '',
              voiceKeyAudioSample: '',
              voiceKeyAudioSampleFileName: '',
              originalFileName: '',
            },
          ];
        } else if (f === 'open_api_keys') {
          out[f] = [{ name: '', api_key: '', model: '' }]; // Add model field for OpenAI
        } else if (f === 'heygen_api_keys') {
          out[f] = [{ name: '', api_key: '', enableHighQuality: false }]; // Add enableHighQuality for Heygen
        } else {
          out[f] = [{ name: '', api_key: '' }];
        }
      } else {
        // Ensure all file fields are preserved
        out[f] = out[f].map(item => {
          const baseItem = {
            name: item.name || '',
            api_key: item.api_key || '',
            ...(item._id ? { _id: item._id } : {}),
          };

          if (f === 'avatar_api_keys') {
            return {
              ...baseItem,
              avatarImage: item.avatarImage || '',
              avatarImageFileName: item.avatarImageFileName || '',
              avatarLoopVideo: item.avatarLoopVideo || '',
              avatarLoopVideoFileName: item.avatarLoopVideoFileName || '',
              originalFileName: item.originalFileName || '',
            };
          } else if (f === 'voice_api_keys') {
            return {
              ...baseItem,
              voiceKeyAudioSample: item.voiceKeyAudioSample || '',
              voiceKeyAudioSampleFileName: item.voiceKeyAudioSampleFileName || '',
              originalFileName: item.originalFileName || '',
            };
          } else if (f === 'open_api_keys') {
            return {
              ...baseItem,
              model: item.model || '',
            };
          } else if (f === 'heygen_api_keys') {
            return {
              ...baseItem,
              enableHighQuality: item.enableHighQuality || false, // Include enableHighQuality for Heygen
            };
          } else {
            return baseItem;
          }
        });
      }
    });

    return out;
  };

  const cleanFormData = data => {
    const cleanedData = { ...data };

    Object.keys(cleanedData).forEach(field => {
      cleanedData[field] = cleanedData[field]
        .filter(k => k.api_key && k.api_key.trim() !== '' && k.name && k.name.trim() !== '')
        .map(k => {
          const baseFields = {
            ...(k._id ? { _id: k._id } : {}),
            name: k.name.trim(),
            api_key: k.api_key.trim(),
          };

          // Include all file-related fields for avatar keys
          if (field === 'avatar_api_keys') {
            return {
              ...baseFields,
              ...(k.avatarImage ? { avatarImage: k.avatarImage } : {}),
              ...(k.avatarImageFileName ? { avatarImageFileName: k.avatarImageFileName } : {}),
              ...(k.avatarLoopVideo ? { avatarLoopVideo: k.avatarLoopVideo } : {}),
              ...(k.avatarLoopVideoFileName
                ? { avatarLoopVideoFileName: k.avatarLoopVideoFileName }
                : {}),
              ...(k.originalFileName ? { originalFileName: k.originalFileName } : {}),
            };
          }
          // Include all file-related fields for voice keys
          else if (field === 'voice_api_keys') {
            return {
              ...baseFields,
              ...(k.voiceKeyAudioSample ? { voiceKeyAudioSample: k.voiceKeyAudioSample } : {}),
              ...(k.voiceKeyAudioSampleFileName
                ? { voiceKeyAudioSampleFileName: k.voiceKeyAudioSampleFileName }
                : {}),
              ...(k.originalFileName ? { originalFileName: k.originalFileName } : {}),
            };
          } else if (field === 'open_api_keys') {
            // Include model field for OpenAI keys
            return {
              ...baseFields,
              ...(k.model ? { model: k.model } : {}),
            };
          } else if (field === 'heygen_api_keys') {
            // Include enableHighQuality field for Heygen keys
            return {
              ...baseFields,
              enableHighQuality: k.enableHighQuality || false,
            };
          } else {
            // For other keys, don't include additional fields
            return baseFields;
          }
        });
    });

    console.log('Cleaned Data being sent to backend:', JSON.stringify(cleanedData, null, 2));
    return cleanedData;
  };


  const validateFormData = data => {
    for (const field of Object.keys(data)) {
      for (const item of data[field]) {
        if (
          (!item.name || item.name.trim() === '') &&
          (!item.api_key || item.api_key.trim() === '')
        ) {
          // completely empty → ignore
          continue;
        }
        if (!item.name || !item.api_key) {
          return field; // ❌ invalid entry
        }
        // Optional: Add model validation for OpenAI keys
        if (field === 'open_api_keys' && (!item.model || item.model.trim() === '')) {
          return 'open_api_keys_model'; // Custom error for model field
        }
      }
    }
    return null;
  };

  const hasDuplicates = data => {
    for (const field of Object.keys(data)) {
      if (field === 'heygen_api_keys') continue;

      const arr = (data[field] || [])
        .map(k => (typeof k.api_key === 'string' ? k.api_key.trim().toLowerCase() : ''))
        .filter(k => k !== '');

      if (new Set(arr).size !== arr.length) return field;
    }
    return null;
  };

  /** ---------- CRUD ---------- */
  const fetchUserDetails = async () => {
    if (!id) return;
    setLoading(true);

    try {
      const res = await fetch(`/auth/user/apiKeys/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch');

      //// Speech To Text Fetch ////
      const safeSpeechOptions = {
        javascript: {
          status: data.speechToTextTool?.javascript?.status ?? true,
          name: data.speechToTextTool?.javascript?.name ?? 'Javascript',
          label: 'Javascript',
        },
        google: {
          status: data.speechToTextTool?.google?.status ?? true,
          name: data.speechToTextTool?.google?.name ?? 'Google Speech API',
          label: 'Google Speech API',
        },
      };
      setSpeechToTextOptions(safeSpeechOptions);
      setInitialSpeech(safeSpeechOptions); // ✅ separate snapshot

      //// LLM Options Fetch ////
      const safeLlmOptions = {
        openai: {
          status: data.llmOptions?.openai?.status ?? true,
          name: data.llmOptions?.openai?.name ?? 'OpenAI',
          label: 'OpenAI',
        },
        motive: {
          status: data.llmOptions?.motive?.status ?? true,
          name: data.llmOptions?.motive?.name ?? 'Motive Intelligence',
          label: 'Motive Intelligence',
        },
      };
      setLlmOptions(safeLlmOptions);
      setInitialLlm(safeLlmOptions); // ✅ separate snapshot
    } catch (err) {
      console.error('Error fetching keys:', err);
      showErrorToast('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserKeyDetails = async () => {
    try {
      const res = await fetch(`/api/api-keys/get-api-keys`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch');

      if (data) {
        const safe = ensureApiArrays(data);
        // 👇 Do NOT strip _id – just keep it as received
        setFormData({
          heygen_api_keys: safe.heygen_api_keys,
          avatar_api_keys: safe.avatar_api_keys,
          voice_api_keys: safe.voice_api_keys,
          open_api_keys: safe.open_api_keys,
          weather_api_keys: safe.weather_api_keys,
        });
        setDocExists(true);
      } else {
        setDocExists(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddField = field => {
    setFormData(prev => {
      let newField;

      if (field === 'avatar_api_keys') {
        newField = {
          name: '',
          api_key: '',
          avatarImage: '',
          avatarImageFileName: '',
          avatarLoopVideo: '',
          avatarLoopVideoFileName: '',
          originalFileName: '',
        };
      } else if (field === 'voice_api_keys') {
        newField = {
          name: '',
          api_key: '',
          voiceKeyAudioSample: '',
          voiceKeyAudioSampleFileName: '',
          originalFileName: '',
        };
      } else if (field === 'open_api_keys') {
        newField = {
          name: '',
          api_key: '',
          model: '', // Add model field for new OpenAI keys
        };
      } else {
        newField = {
          name: '',
          api_key: '', enableHighQuality: false,
        };
      }

      return {
        ...prev,
        [field]: [...prev[field], newField],
      };
    });
  };


  const handleRemoveField = (field, index) => {
    setFormData(prev => {
      const updated = [...prev[field]];
      if (updated.length > 1) {
        updated.splice(index, 1);
      } else {
        // Reset if only one left, preserving the correct structure with all fields
        if (field === 'avatar_api_keys') {
          updated[0] = {
            name: '',
            api_key: '',
            avatarImage: '',
            avatarImageFileName: '',
            avatarLoopVideo: '',
            avatarLoopVideoFileName: '',
            originalFileName: '',
          };
        } else if (field === 'voice_api_keys') {
          updated[0] = {
            name: '',
            api_key: '',
            voiceKeyAudioSample: '',
            voiceKeyAudioSampleFileName: '',
            originalFileName: '',
          };
        } else if (field === 'open_api_keys') {
          updated[0] = {
            name: '',
            api_key: '',
            model: '', // Reset model field for OpenAI
          };
        } else if (field === 'heygen_api_keys') {
          updated[0] = {
            name: '',
            api_key: '',
            enableHighQuality: false, // Reset enableHighQuality for Heygen
          };
        } else {
          updated[0] = { name: '', api_key: '' };
        }
      }
      return { ...prev, [field]: updated };
    });
  };

  const saveKeys = async e => {
    e.preventDefault();

    const invalidField = validateFormData(formData);

    if (invalidField) {
      if (invalidField === 'open_api_keys_model') {
        showErrorToast('OpenAI Model is required for OpenAI API keys');
      } else {
        showErrorToast(`Both Name and API Key are required for ${invalidField.replace(/_/g, ' ')}`);
      }
      return;
    }

    const cleanedData = cleanFormData(formData);

    const duplicateField = hasDuplicates(cleanedData);

    if (duplicateField) {
      showErrorToast(`Duplicate keys found in ${duplicateField.replace(/_/g, ' ')}`);

      return;
    }

    try {
      const res = await fetch(`/api/api-keys/add-api-keys`, {
        // Remove /${id}

        method: 'POST',

        headers: {
          'Content-Type': 'application/json',

          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify(cleanedData),
      });

      const data = await res.json();

      if (res.ok) {
        showMsgSuccessToast('API Keys saved successfully');

        await fetchUserKeyDetails();

        await fetchAvailableKeys();
      }
    } catch (error) {
      console.error(error);

      showErrorToast('Failed to save api keys');
    }
  };

  /** ---------- Effects ---------- */
  useEffect(() => {
    fetchUserDetails();
    fetchUserKeyDetails();
  }, [id]);

  /** ---------- Input Handlers ---------- */
  const handleArrayChange = (field, index, key, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[index] = { ...arr[index], [key]: value }; // _id stays untouched
      return { ...prev, [field]: arr };
    });
  };




  ////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////---------------Speech To Text Options---------------------------/////////////////////

  const [speechToTextOptions, setSpeechToTextOptions] = useState({
    javascript: { status: true, name: '', label: 'Javascript' },
    google: { status: true, name: '', label: 'Google Speech Api' },
  });

  console.log(speechToTextOptions);

  const handleToggle = key =>
    setSpeechToTextOptions(prev => ({
      ...prev,
      [key]: { ...prev[key], status: !prev[key].status },
    }));

  const handleSpeechNameChange = (key, value) => {
    setSpeechToTextOptions(prev => ({
      ...prev,
      [key]: { ...prev[key], name: value },
    }));
  };

  const [saving, setSaving] = useState(false);

  const isSpeechToTextChanged = () => {
    if (!initialSpeech) return true;

    return (
      initialSpeech.javascript.status !== speechToTextOptions.javascript.status ||
      initialSpeech.javascript.name !== speechToTextOptions.javascript.name ||
      initialSpeech.google.status !== speechToTextOptions.google.status ||
      initialSpeech.google.name !== speechToTextOptions.google.name
    );
  };

  const isLlmChanged = () => {
    if (!initialLlm) return true;

    return (
      initialLlm.openai.status !== llmOptions.openai.status ||
      initialLlm.openai.name !== llmOptions.openai.name ||
      initialLlm.motive.status !== llmOptions.motive.status ||
      initialLlm.motive.name !== llmOptions.motive.name
    );
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedClient) {
      showErrorToast('No client selected');
      return;
    }
    if (!isSpeechToTextChanged()) {
      showErrorToast('No changes to save');
      return;
    }

    console.log(speechToTextOptions);

    setSaving(true);
    try {
      const res = await fetch(`/auth/users/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ speechToTextTool: speechToTextOptions }),
      });
      const data = await res.json();
      if (res.ok) {
        showMsgSuccessToast('Speech to Text Options saved successfully');
        await fetchUserDetails(); // ✅ Refresh keys from DB
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('Save error:', err);
      showErrorToast('An error occurred: ' + err.message);
    }
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////------------------LLM Options ----------------------------//////////////////////////////
  const [llmOptions, setLlmOptions] = useState({
    openai: { status: true, name: '', label: 'OpenAI' },
    motive: { status: true, name: '', label: 'Motive Intelligence' },
  });

  const handleLlmOptionsToggle = key =>
    setLlmOptions(prev => ({
      ...prev,
      [key]: { ...prev[key], status: !prev[key].status },
    }));

  // For LLM
  const handleLlmNameChange = (key, value) => {
    setLlmOptions(prev => ({
      ...prev,
      [key]: { ...prev[key], name: value },
    }));
  };

  const handleLlmOptionsSave = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      const res = await fetch(`/auth/users/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ llmOptions }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');

      // ✅ Save current state as initial snapshot
      setInitialLlm({ ...llmOptions });
      showMsgSuccessToast('LLM settings saved');
    } catch (err) {
      showErrorToast(err.message || 'Failed to save LLM settings');
    } finally {
      setSaving(false);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////---------------------For MOtive integration-------------/////////////////////////






  const createEmptyConfig = () => ({
    motive_name: '',
    motive_organization: '',
    motive_space: '',
    motive_agent: '',
    motive_deployment: '',
    motive_temprature: 2,
    motive_function: '',
    motive_knowledge: '',
    motive_baseurl: 'https://preview.az.motivecloud.dev/api',
    motive_outputtext: 'I have not found this text',
    motive_client_id: '',
    motive_client_secret: '',
    motivetoken: '',
    token_expires_at: null,
  });

  const handleMotiveConfigsChange = newConfigs => {

    setMotiveConfigs(newConfigs);

    setMotiveHasChanges(true);

  };




  const handleMotiveSelectionChange = selectedOption => {

    console.log('Selected Config:', selectedOption);

    setSelectedConfig(selectedOption);

  };



  useEffect(() => {

    fetchMotiveConfigs();

  }, []);


  const fetchMotiveConfigs = async () => {
    setMotiveLoading(true);
    try {
      const response = await fetch('/api/motive-integration/get-configs');
      const data = await response.json();

      if (response.ok) {
        if (data.configs && data.configs.length > 0) {
          const configsWithDefaults = data.configs.map(config => ({
            ...config,
            motive_temprature: config.motive_temprature || 2,
            motive_outputtext: config.motive_outputtext || 'I have not found this text',
            motive_baseurl: config.motive_baseurl || 'https://preview.az.motivecloud.dev/api',
          }));
          setMotiveConfigs(configsWithDefaults);
        } else {
          setMotiveConfigs([createEmptyConfig()]);
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      showErrorToast('Failed to load configurations');
      setMotiveConfigs([createEmptyConfig()]);
    } finally {
      setMotiveLoading(false);
    }
  };

  const handleConfigChange = (index, field, value) => {
    const updatedConfigs = [...motiveConfigs];

    if (field === 'motive_baseurl' && value === '') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: '',
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value,
      };
    }

    setMotiveConfigs(updatedConfigs);

    if (validationErrors[`${index}-${field}`]) {
      const newErrors = { ...validationErrors };
      delete newErrors[`${index}-${field}`];
      setValidationErrors(newErrors);
    }
  };

  const handleRemoveConfig = async index => {
    const config = motiveConfigs[index];

    if (config._id) {
      try {
        const response = await fetch(`/api/motive-integration/delete-config/${config._id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          showMsgSuccessToast('Configuration deleted successfully');
        }
      } catch (error) {
        showErrorToast('Failed to delete configuration');
        return;
      }
    }

    const newConfigs = [...motiveConfigs];
    if (newConfigs.length === 1) {
      newConfigs[0] = createEmptyConfig();
    } else {
      newConfigs.splice(index, 1);
    }
    setMotiveConfigs(newConfigs);
  };

  const validateConfig = (config, index) => {
    const requiredFields = [
      'motive_name',
      'motive_organization',
      'motive_space',
      'motive_agent',
      'motive_deployment',
      'motive_baseurl',
      'motive_client_id',
      'motive_client_secret',
    ];

    const errors = {};
    let isValid = true;

    for (const field of requiredFields) {
      if (!config[field] || config[field].toString().trim() === '') {
        errors[`${index}-${field}`] =
          `${field.replace('motive_', '').replace('_', ' ')} is required`;
        isValid = false;
      }
    }

    // Validate URL format
    if (config.motive_baseurl && config.motive_baseurl.trim() !== '') {
      try {
        new URL(config.motive_baseurl);
      } catch (error) {
        errors[`${index}-motive_baseurl`] = 'Please enter a valid URL';
        isValid = false;
      }
    }

    setValidationErrors(prev => ({ ...prev, ...errors }));
    return isValid;
  };

  const handleSaveConfig = async index => {
    const config = motiveConfigs[index];

    // Ensure temperature and output text always have the correct values
    const configWithDefaults = {
      ...config,
      motive_temprature: 2,
      motive_outputtext: 'I have not found this text',
    };

    if (!validateConfig(configWithDefaults, index)) {
      showErrorToast('Please fill in all required fields correctly');
      return;
    }

    setSavingStates(prev => ({ ...prev, [index]: true }));

    try {
      const saveResponse = await fetch('/api/motive-integration/save-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configWithDefaults),
      });

      const saveData = await saveResponse.json();

      if (!saveResponse.ok) {
        throw new Error(saveData.message || 'Failed to save configuration');
      }

      showMsgSuccessToast('Configuration saved successfully!');

      // Update config with the latest data
      const updatedConfigs = [...motiveConfigs];
      updatedConfigs[index] = {
        ...saveData.config,
        // Ensure the fixed values are maintained
        motive_temprature: 2,
        motive_outputtext: 'I have not found this text',
      };
      setMotiveConfigs(updatedConfigs);

      // Clear validation errors
      const newErrors = { ...validationErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${index}-`)) {
          delete newErrors[key];
        }
      });
      setValidationErrors(newErrors);
    } catch (error) {
      console.error('Save error:', error);
      showErrorToast(error.message || 'Failed to save configuration');
    } finally {
      setSavingStates(prev => ({ ...prev, [index]: false }));
    }
  };


  ///////////////////////////////////////////////////////


  useEffect(() => {

    if (selectedClient) {

      fetchClientSettings(selectedClient);

      fetchUserAssignedMotiveConfig();

    }

  }, [selectedClient]);



  const fetchUserAssignedMotiveConfig = async () => {

    if (!selectedClient) return;



    try {

      const res = await fetch(`/api/motive-integration/get-motive-assigned-user/${selectedClient}`);

      const data = await res.json();



      console.log('user adta', data);



      if (res.ok && data.user && data?.user?.assigned_motive_integration) {

        const assignedConfig = motiveConfigs.find(

          config => config._id.toString() === data.user?.assigned_motive_integration._id.toString()

        );



        if (assignedConfig) {

          setSelectedConfig({

            value: assignedConfig._id,

            label: `${assignedConfig?.motive_name} (Agent: ${assignedConfig?.motive_agent})${assignedConfig?.motive_organization ? ` - ${assignedConfig?.motive_organization}` : ''}`,

          });

        }

      } else {

        setSelectedConfig(null);

      }

    } catch (error) {

      console.error('Error fetching assigned motive config:', error);

      setSelectedConfig(null);

    }

  };



  const saveMotiveConfigToUser = async selectedConfig => {

    if (!selectedClient) {

      showErrorToast('Please select a client first');

      return;

    }



    setLoading(true);

    try {

      const res = await fetch(`/api/motive-integration/assign-to-user/${selectedClient}`, {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

        },

        body: JSON.stringify({

          selectedConfigId: selectedConfig ? selectedConfig.value : null, // Send null when clearing

        }),

      });



      const data = await res.json();



      if (res.ok) {

        if (selectedConfig) {

          showMsgSuccessToast(

            `Motive configuration assigned to ${selectedClientInfo?.company_name}`

          );

        } else {

          showMsgSuccessToast(

            `Motive configuration assignment removed from ${selectedClientInfo?.company_name}`

          );

        }

        console.log('Motive assignment successful:', data);



        // Optionally, you can fetch the updated user data to confirm the assignment

        await fetchUserAssignedMotiveConfig();

      } else {

        showErrorToast(data.message || 'Failed to assign Motive configuration');

      }

    } catch (error) {

      console.error('Error assigning Motive configuration:', error);

      showErrorToast('Failed to assign Motive configuration');

    } finally {

      setLoading(false);

    }

  };



  ///////////////////////////////////////////////////////////////////////////////////////////////

  const handleStreamingConfigChange = (field, value) => {
    setAssignedStreamingConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAiStreamingMinSave = async () => {
    try {
      const res = await axios.put(`/auth/streaming-config/${selectedClient}`, {
        monthlyOpenAiTotalToken: assignedStreamingConfig.monthlyOpenAiTotalToken,
        liveStreamingMint: assignedStreamingConfig.liveStreamingMint,
      });

      showMsgSuccessToast('Streaming Settings Saved Successfully');
      console.log('Save Response:', res.data);
    } catch (error) {
      console.log(error);
      showErrorToast('Failed to Save Settings');
    }
  };

  const fetchAiStreamingConfig = async () => {
    try {
      if (selectedClient) {
        const res = await axios.get(`/auth/streaming-config/${selectedClient}`);

        if (res.data?.data) {
          setAssignedStreamingConfig(prev => ({
            ...prev,
            monthlyOpenAiTotalToken: res.data.data.monthlyOpenAiTotalToken ?? 0,
            liveStreamingMint: res.data.data.liveStreamingMint ?? 0,
          }));
        }
      }
    } catch (error) {
      console.log(error);
      showErrorToast('Failed to load streaming config');
    }
  };

  useEffect(() => {
    fetchAiStreamingConfig();
  }, [selectedClient]);

  ////////////////////////////////////////////////////////////////////////////

  const handleCaptionToggle = (currentStatus) => {
    setCaptionSettings((prev) => ({
      ...prev,
      status: !currentStatus,
    }));
  };



  const handleCaptionsSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      await axios.post(`/api/captions/add-caption`, {
        userId: selectedClient,
        status: captionSettings.status,
      });

      await fetchCaptionStatus();
      showMsgSuccessToast('Caption Settings Saved Successfully')

    } catch (error) {
      console.error("Error saving caption settings:", error);
      alert("Failed to save caption settings.");
    } finally {
      setLoading(false);
    }
  };


  const fetchCaptionStatus = async () => {
    try {
      if (selectedClient) {
        const res = await fetch(`/api/captions/get-caption/${selectedClient}`)
        const data = await res.json();
        setCaptionSettings((prev) => ({
          ...prev, status: data.status
        }
        ))
      }

    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    fetchCaptionStatus()
  }, [selectedClient])

  /////////////////////////////////////////////////////////////////////////////

  // Weather Settings
  const [weatherSettings, setWeatherSettings] = useState({ enabled: false });
  const [selectedWeatherKey, setSelectedWeatherKey] = useState(null);

  const fetchWeatherSettings = async () => {
    try {
      if (selectedClient) {
        const res = await fetch(`/api/weather-settings/get/${selectedClient}`);
        const data = await res.json();
        setWeatherSettings(data || { enabled: false });
        if (data?.weatherApiKeyId || data?.weatherApiKey) {
          // Restore selected key from saved data
          const match = availableKeys.weather?.find(
            k => k._id?.toString() === data.weatherApiKeyId || k.api_key === data.weatherApiKey
          );
          if (match) {
            setSelectedWeatherKey({ value: match._id, label: match.name, apiKey: match.api_key });
          } else if (data.weatherApiKeyName) {
            setSelectedWeatherKey({ value: data.weatherApiKeyId || '', label: data.weatherApiKeyName, apiKey: data.weatherApiKey || '' });
          }
        } else {
          setSelectedWeatherKey(null);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleWeatherToggle = () => {
    setWeatherSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleWeatherSave = async () => {
    try {
      setLoading(true);
      await axios.post(`/api/weather-settings/save/${selectedClient}`, {
        ...weatherSettings,
        weatherApiKey: selectedWeatherKey?.apiKey || '',
        weatherApiKeyId: selectedWeatherKey?.value || '',
        weatherApiKeyName: selectedWeatherKey?.label || '',
      });
      showMsgSuccessToast('Weather Settings Saved Successfully');
    } catch (error) {
      console.log(error);
      showErrorToast('Failed to save weather settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeatherSettings();
  }, [selectedClient, availableKeys.weather]);

  /////////////////////////////////////////////////////////////////////////////

  return (
    <div className="main-container py-4 fade-in">
      {/* Enhanced Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb bg-white rounded-pill px-4 py-2 shadow-sm">
          <li className="breadcrumb-item">
            <Link to="/admin/dashboard" className="text-decoration-none text-primary">
              <i className="fas fa-home me-2"></i>
              Dashboard
            </Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            <i className="fas fa-cog me-2"></i>
            Global Settings
          </li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-12">
          <div className="page-header">
            <div className="row">
              <div className="col-md-12 col-sm-12">
                <div className="title">
                  <h4>Global Settings</h4>
                  <p className="text-muted mb-0 fs-5">
                    Manage system-wide configurations and client-specific settings with advanced
                    controls
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Tab Navigation */}
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'clients' ? 'active' : ''}`}
                onClick={() => setActiveTab('clients')}
              >
                <span style={{ marginRight: '10px', fontSize: '20px' }}> <FaUsers /></span>
                Client Settings
                <span className="badge bg-light text-dark ms-2">{clients.length}</span>
              </button>
            </li>


            {/* <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "login-customization" ? "active" : ""}`}
                onClick={() => setActiveTab("login-customization")}
              >
                <i className="fas fa-paint-brush me-2"></i>
                Login Customization
                <span className="badge bg-light text-dark ms-2">New</span>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "slug-management" ? "active" : ""}`}
                onClick={() => setActiveTab("slug-management")}
              >
                <i className="fas fa-link me-2"></i>
                Slug Management
                <span className="badge bg-light text-dark ms-2">{clients.length}</span>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "original-settings" ? "active" : ""}`}
                onClick={() => setActiveTab("original-settings")}
              >
                <i className="fas fa-cogs me-2"></i>
                Original Settings
                <span className="badge bg-light text-dark ms-2">Legacy</span>
              </button>
            </li> */}

            {/* Multiple APi Keys */}
            <AddApiKeysTab activeTab={activeTab} setActiveTab={setActiveTab} />

            <MotiveIntegrationTab activeTab={activeTab} setActiveTab={setActiveTab} />


            {/* Terms and Conditions */}
            <TermsConditions activeTab={activeTab} setActiveTab={setActiveTab} />



            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'languages' ? 'active' : ''}`}
                onClick={() => setActiveTab('languages')}
              >
                <span style={{ marginRight: '10px', fontSize: '20px' }}><FaLanguage /></span>
                Speech Languages
                <span className="badge bg-light text-dark ms-2">{selectedLanguages.length}</span>
              </button>
            </li>



            {/* <ChatGptModelIntegration activeTab={activeTab} setActiveTab={setActiveTab} /> */}


          </ul>
          {/* Enhanced Alert Messages */}

          {/* Enhanced Languages Tab */}
          {activeTab === 'languages' && (
            <div className="card fade-in">
              <div className="card-header">
                <h5 className="card-title">
                  <span style={{ marginRight: '10px', fontSize: '25px' }}><FaLanguage /></span>
                  Speech Language Configuration
                </h5>
                <p className="card-text mb-0">
                  Select multiple languages that will be available for speech recognition and
                  synthesis across your platform
                </p>
              </div>

              <div className="card-body">
                {/* Enhanced Selected Languages Section */}
                <div className="language-selection-area">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 text-white">

                      Selected Languages ({selectedLanguages.length})
                    </h6>
                    <div className="text-white-50">
                      <small>Click on badges to remove languages</small>
                    </div>
                  </div>
                  <div className="selected-languages-container">
                    {selectedLanguages.length === 0 ? (
                      <div className="text-center py-4">

                        <p className="mb-0 opacity-75">No languages selected yet</p>
                        <small className="opacity-50">
                          Choose from the available languages below
                        </small>
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap">
                        {selectedLanguages.map(code => {
                          const language = AVAILABLE_LANGUAGES.find(lang => lang.code === code);
                          return language ? (
                            <span key={code} className="language-badge slide-in">
                              {/*   <span className="me-2">{language.flag}</span> */}
                              <Flag code={language.flag} />
                              <span className="me-2 fw-bold">{language.code}</span>
                              <span className="me-2">{language.name}</span>
                              <button
                                type="button"
                                className="btn-close"
                                onClick={() => removeLanguage(code)}
                                title={`Remove ${language.name}`}
                              ></button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {message && (
                  <div
                    className={`alert-enhanced ${message.includes('Error') ? 'alert-danger-enhanced' : 'alert-success-enhanced'
                      } slide-in`}
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className={`fas ${message.includes('Error') ? 'fa-exclamation-triangle' : 'fa-check-circle'
                          } me-3 fs-5`}
                      ></i>
                      <div className="flex-grow-1">
                        <strong>{message.includes('Error') ? 'Error!' : 'Success!'}</strong>
                        <div>{message}</div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setMessage('')}
                      ></button>
                    </div>
                  </div>
                )}

                {/* Enhanced Language Search */}
                <div className="search-container">

                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Search languages by name or code (e.g., 'English' or 'en-US')..."
                    value={languageSearch}
                    onChange={e => setLanguageSearch(e.target.value)}
                  />
                </div>

                {/* Enhanced Available Languages */}
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 fw-bold text-primary">

                      Available Languages
                    </h6>
                    <div className="text-muted">
                      <small>
                        Showing {filteredLanguages.length} of {AVAILABLE_LANGUAGES.length} languages
                      </small>
                    </div>
                  </div>
                  <div className="languages-container">
                    {filteredLanguages.map(language => (
                      <div
                        key={language.code}
                        className="language-item d-flex align-items-center"
                        onClick={() => handleLanguageToggle(language.code)}
                      >
                        <input
                          className="language-checkbox"
                          type="checkbox"
                          id={language.code}
                          checked={selectedLanguages.includes(language.code)}
                        // onChange={() => handleLanguageToggle(language.code)}
                        />
                        {/* <span className="language-flag">{language.flag}</span> */}
                        <Flag code={language.flag} />
                        <div className="language-info flex-grow-1">
                          <h6 className="mb-1">{language.name}</h6>
                          <small className="text-muted">{language.code}</small>
                        </div>
                        {/* {selectedLanguages.includes(language.code) && (
                          <i className="fas fa-check-circle text-success"></i>
                        )} */}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Enhanced Save Button */}
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-enhanced btn-primary-enhanced px-5"
                    onClick={saveLanguageSettings}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner me-2"></span>
                        Saving Configuration...
                      </>
                    ) : (
                      <>

                        Save Language Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Client Settings Tab */}
          {activeTab === 'clients' && (
            <>
              <div className="card fade-in">
                <div className="card-header">
                  <h5 className="card-title">

                    Client-Specific Settings
                  </h5>
                  <p className="card-text mb-0">
                    Configure individual client settings including limits, features, and preferences
                  </p>
                </div>
                <div className="card-body">
                  {/* Enhanced Client Selection */}
                  <div className="settings-section" style={{ padding: '2rem' }}>
                    <h6>
                      <i className="fas fa-user-cog me-2"></i>
                      Select Client
                    </h6>
                    <select
                      className="form-select form-select-enhanced"
                      value={selectedClient}
                      onChange={e => setSelectedClient(e.target.value)}
                    >
                      <option value="">Choose a client to configure their settings</option>
                      {clients.map(client => (
                        <option key={client._id} value={client._id}>
                          <i className="fas fa-building me-2"></i>
                          {client?.company_name}
                        </option>
                      ))}
                    </select>
                    {selectedClientInfo && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="row">
                          <div className="col-md-3">
                            <strong>Company Name:</strong> {selectedClientInfo.company_name}
                          </div>

                          <div className="col-md-3">
                            <strong>Email:</strong> {selectedClientInfo.email}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedClient && (
                <div className="fade-in">
                  <FeatureAccessPanel
                    clientSettings={clientSettings}
                    setClientSettings={setClientSettings}
                  />
                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={saveClientSettings}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner me-2"></span>
                          Saving Feature Access Control Settings...
                        </>
                      ) : (
                        <>

                          Save Feature Access Control Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {selectedClient && (
                <>
                  <div className="card fade-in" style={{ marginTop: '3rem' }}>
                    <div className="card-header">
                      <h5 className="card-title">
                        <span style={{ marginRight: '10px', fontSize: '24px' }}> <CiStreamOn /></span>
                        AI & Streaming Settings
                      </h5>
                      <p className="card-text mb-0">
                        Assign a Monthly OpenAI Total Token and Live Streaming Mint to this user
                      </p>
                    </div>
                    <div className="card-body">
                      <div className="form-group">
                        <label className="form-label fw-semibold">
                          Monthly OpenAI Total Token:
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={assignedStreamingConfig.monthlyOpenAiTotalToken}
                          onChange={e =>
                            handleStreamingConfigChange(
                              'monthlyOpenAiTotalToken',
                              Number(e.target.value)
                            )
                          }
                          min="0"
                          style={{ borderRadius: '6px', padding: '10px' }}
                        />
                      </div>
                      <Grid item xs={12} md={6}>
                        <div className="form-group">
                          <label className="form-label fw-semibold">Live Streaming Mint:</label>
                          <input
                            type="number"
                            className="form-control"
                            value={assignedStreamingConfig.liveStreamingMint}
                            onChange={e =>
                              handleStreamingConfigChange(
                                'liveStreamingMint',
                                Number(e.target.value)
                              )
                            }
                            min="0"
                            style={{ borderRadius: '6px', padding: '10px' }}
                          />
                        </div>
                      </Grid>
                    </div>
                  </div>{' '}
                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={handleAiStreamingMinSave}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner me-2"></span>
                          Saving Ai & Streaming Settings...
                        </>
                      ) : (
                        <>

                          Save Ai & Streaming Settings
                        </>
                      )}
                    </button>
                  </div>{' '}
                </>
              )}

              {
                selectedClient && (
                  <div className="fade-in" style={{ marginTop: '3rem' }}>
                    <CaptionFileSettings captionSettings={captionSettings}
                      handleToggle={handleCaptionToggle} />

                    <div className="d-flex justify-content-end pt-4">
                      <button
                        className="btn btn-enhanced btn-primary-enhanced px-5"
                        onClick={handleCaptionsSubmit}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="loading-spinner me-2"></span>
                            Saving Captions Settings...
                          </>
                        ) : (
                          <>

                            Save Caption Settings
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )
              }

              {/* Weather Report Settings */}
              {selectedClient && (
                <div className="fade-in" style={{ marginTop: '3rem' }}>
                  <div className="card shadow-sm border-0 fade-in">
                    <div className="card-header text-white">
                      <h5 className="card-title mb-0">
                        <span style={{ marginRight: '5px', fontSize: '20px' }}><WiDaySunny /></span>
                        Weather Report Settings
                      </h5>
                      <small>Enable or disable weather widget on the main menu for this client.</small>
                    </div>
                    <div className="card-body">
                      <div className="mb-4 p-3 border rounded-2 shadow-sm" style={{ backgroundColor: '#f9f9f9' }}>
                        <div className="form-group custom-control custom-checkbox mb-2 d-flex align-items-center">
                          <input
                            className="custom-control-input me-2"
                            type="checkbox"
                            id="weather-status"
                            checked={weatherSettings.enabled || false}
                            onChange={handleWeatherToggle}
                          />
                          <label className="custom-control-label fw-semibold mb-0" htmlFor="weather-status" style={{ fontSize: '1rem' }}>
                            Enable Weather Report on Main Menu
                          </label>
                        </div>
                        <small className="text-muted mt-1 d-block">
                          When enabled, a weather widget will be displayed on the main menu screen.
                        </small>
                      </div>

                      {weatherSettings.enabled && (
                        <div className="form-group mt-3">
                          <label className="form-label fw-semibold">Assign Weather API Key:</label>
                          <Select
                            options={(availableKeys.weather || []).map(k => ({ value: k._id, label: k.name, apiKey: k.api_key }))}
                            value={selectedWeatherKey}
                            onChange={option => setSelectedWeatherKey(option)}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select a Weather API key..."
                            isClearable
                          />
                          <small className="form-text text-muted">
                            Select the OpenWeatherMap API key to use for this client. Add keys in the "API Keys" tab first.
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={handleWeatherSave}
                      disabled={loading}
                    >
                      {loading ? (
                        <><span className="loading-spinner me-2"></span>Saving Weather Settings...</>
                      ) : (
                        <>Save Weather Settings</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Multiple Api Keys Settings */}
              {selectedClient && (
                <div className="fade-in" style={{ marginTop: '3rem' }}>
                  <div className="card shadow-sm border-0 fade-in">
                    <div className="card-header text-white">
                      <h5 className="card-title mb-0">
                        <IoKeySharp /> Manage API Keys
                      </h5>

                      <p className="card-text small mb-0">Assign Api Keys to User</p>
                    </div>

                    <div className="card-body">
                      {/* Heygen API Keys */}

                      <div className="form-group mb-4">
                        <label>Heygen API Keys:</label>

                        <Select
                          isMulti
                          options={formatOptions(availableKeys.heygen)}
                          value={formatOptions(getSelectedOptions('heygen'))}
                          onChange={selected => handleKeySelectionChange('heygen', selected)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select Heygen API keys..."
                        />

                        <small className="form-text text-muted">
                          Select one or multiple Heygen API keys to assign to this user
                        </small>
                      </div>

                      {/* Avatar API Keys */}

                      <div className="form-group mb-4">
                        <label>Avatar API Keys:</label>

                        <Select
                          isMulti
                          options={formatOptions(availableKeys.avatar)}
                          value={formatOptions(getSelectedOptions('avatar'))}
                          onChange={selected => handleKeySelectionChange('avatar', selected)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select Avatar API keys..."
                        />

                        <small className="form-text text-muted">
                          Select one or multiple Avatar API keys to assign to this user
                        </small>
                      </div>

                      {/* Voice API Keys */}

                      <div className="form-group mb-4">
                        <label>Voice API Keys:</label>

                        <Select
                          isMulti
                          options={formatOptions(availableKeys.voice)}
                          value={formatOptions(getSelectedOptions('voice'))}
                          onChange={selected => handleKeySelectionChange('voice', selected)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select Voice API keys..."
                        />

                        <small className="form-text text-muted">
                          Select one or multiple Voice API keys to assign to this user
                        </small>
                      </div>

                      {/* OpenAI API Keys */}

                      <div className="form-group mb-4">
                        <label>OpenAI API Keys:</label>

                        <Select
                          isMulti
                          options={formatOptions(availableKeys.openai)}
                          value={formatOptions(getSelectedOptions('openai'))}
                          onChange={selected => handleKeySelectionChange('openai', selected)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select OpenAI API keys..."
                        />

                        <small className="form-text text-muted">
                          Select one or multiple OpenAI API keys to assign to this user
                        </small>
                      </div>

                      {/* Weather API Keys */}
                      <div className="form-group mb-4">
                        <label>Weather API Keys:</label>
                        <Select
                          isMulti
                          options={formatOptions(availableKeys.weather || [])}
                          value={formatOptions(getSelectedOptions('weather'))}
                          onChange={selected => handleKeySelectionChange('weather', selected)}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select Weather API keys..."
                        />
                        <small className="form-text text-muted">
                          Select one or multiple Weather API keys to assign to this user
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={saveAssignedKeys}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner me-2"></span>
                          Assigning API Keys...
                        </>
                      ) : (
                        <>

                          Assign API Keys
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Speech to Text Option Settings */}
              {selectedClient && (
                <div className="fade-in" style={{ marginTop: '3rem' }}>
                  <SpeechToTextOptions
                    loading={loading}
                    handleSubmit={handleSubmit}
                    handleToggle={handleToggle}
                    speechToTextOptions={speechToTextOptions}
                    handleNameChange={handleSpeechNameChange}
                  />

                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner me-2"></span>
                          Saving Text to Speech Provider Settings...
                        </>
                      ) : (
                        <>

                          Save Text to Speechs Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* LLM Options Settings */}
              {selectedClient && (
                <div className="fade-in" style={{ marginTop: '3rem' }}>
                  <LlmOptionsSettings
                    loading={loading}
                    llmOptions={llmOptions}
                    handleToggle={handleLlmOptionsToggle}
                    handleNameChange={handleLlmNameChange}
                  />
                  <div className="d-flex justify-content-end pt-4">
                    <button
                      className="btn btn-enhanced btn-primary-enhanced px-5"
                      onClick={handleLlmOptionsSave}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner me-2"></span>
                          Saving LLM options Settings...
                        </>
                      ) : (
                        <>

                          Save LLM options Settings
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}






              {selectedClient && (
                <>
                  <div className="card fade-in mt-4">
                    <div className="card-header">
                      <h5 className="card-title">
                        <span style={{ marginRight: '10px' }}><FaRobot /></span>
                        Motive Configuration Assignment
                      </h5>
                      <p className="card-text mb-0">
                        Assign a Motive AI configuration to this client. Clear selection to remove
                        assignment.
                      </p>
                    </div>

                    <div className="card-body">
                      {motiveConfigs && motiveConfigs.length > 0 ? (

                        <div className="form-group">

                          <label className="form-label fw-semibold">

                            Select Motive Configuration

                          </label>

                          <Select

                            options={motiveConfigs

                              .filter(config => config.motive_name && config.motive_agent) // Filter out empty configs

                              .map(config => ({

                                value: config._id || config.id,

                                label: `${config.motive_name} (Agent: ${config.motive_agent})${config.motive_organization ? ` - ${config.motive_organization}` : ''}`,

                              }))}

                            value={selectedConfig}

                            onChange={handleMotiveSelectionChange}

                            className="react-select-container"

                            classNamePrefix="react-select"

                            placeholder="Choose a Motive configuration..."

                            isClearable

                            isSearchable

                          />

                          <small className="form-text text-muted">

                            Select a Motive configuration to assign to this client. Clear selection

                            to remove current assignment.

                          </small>

                        </div>

                      ) : (

                        <div className="text-center py-4">

                          <FaRobot />

                          <h6 className="text-muted">No Motive Configurations Available</h6>

                          <p className="text-muted small mb-0">

                            Please add Motive configurations in the "Motive Integration" tab first.

                          </p>

                        </div>

                      )}

                    </div>

                  </div>



                  <div className="d-flex justify-content-end pt-4">

                    <button

                      className="btn btn-enhanced btn-primary-enhanced px-5"

                      onClick={() => {

                        saveMotiveConfigToUser(selectedConfig);

                      }}

                      disabled={loading || motiveConfigs.length === 0}

                    >

                      {loading ? (

                        <>

                          <span className="loading-spinner me-2"></span>

                          Assigning Motive Configuration...

                        </>

                      ) : (

                        <>

                          <i className="fas fa-save me-2"></i>

                          Assign Motive Configuration

                        </>

                      )}

                    </button>

                  </div>

                </>

              )}

              {selectedClient && (
                <div className="fade-in">
                  <LoginSlugManager selectedClient={selectedClient} clients={clients} />
                  {/* <LoginCustomizationPanel selectedClient={selectedClient} clients={clients} /> */}
                </div>
              )}
            </>
          )}

          {activeTab === 'termsConditions' && <TermsTextEditor />}

          {/* {activeTab === "slug-management" && (
              
                
              
            )} */}

          {/* Add Multiple APi KEys Tab */}

          {activeTab === 'motiveIntegration' && (
            <>
              <div className="fade-in">
                <MotiveIntegrationForm
                  motiveConfigs={motiveConfigs}
                  setMotiveConfigs={setMotiveConfigs}
                  onConfigsChange={handleMotiveConfigsChange}
                  showErrorToast={showErrorToast}
                  showMsgSuccessToast={showMsgSuccessToast}
                  loading={motiveLoading}
                  userId={selectedClient}
                  handleConfigChange={handleConfigChange}
                  handleRemoveConfig={handleRemoveConfig}
                  handleSaveConfig={handleSaveConfig}
                  validationErrors={validationErrors}
                  savingStates={savingStates}
                  fetchMotiveConfigs={fetchMotiveConfigs}
                  motiveLoading={motiveLoading}
                  createEmptyConfig={createEmptyConfig}
                />
              </div>
            </>

          )}

          {activeTab === 'addApiKeys' && (
            <div className="fade-in" style={{ marginTop: '3rem' }}>
              <MultipleApiKeysSettings
                saveKeys={saveKeys}
                formData={formData}
                handleArrayChange={handleArrayChange}
                handleRemoveField={handleRemoveField}
                handleAddField={handleAddField}
                handleFileUpload={handleFileUpload}
                uploadingFiles={uploadingFiles}
                showErrorToast={showErrorToast} // Add this line
              />

              <div className="d-flex justify-content-end pt-4">
                <button
                  className="btn btn-enhanced btn-primary-enhanced px-5"
                  onClick={saveKeys}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner me-2"></span>
                      Saving Api Keys Settings...
                    </>
                  ) : (
                    <>

                      Save Api Keys Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* {activeTab === 'chatgpt' && (
            <div className="fade-in" style={{ marginTop: '3rem' }}>
              <ChatGptIntegration />
            </div>
          )} */}
        </div>
      </div>
      <style jsx>{`
        :root {
          --primary-gradient: #2d3748;
          --success-color: #48bb78;
          /* --success-gradient: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  --warning-color: #ed8936;
  --warning-gradient: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
  --danger-color: #f56565; */
          --info-color: #4299e1;
          --dark-color: #2d3748;
          --light-bg: #f7fafc;
          --card-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          --card-shadow-hover: 0 20px 40px rgba(0, 0, 0, 0.15);
          --border-radius: 12px;
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Global Enhancements */
        body {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
        }

        /* Enhanced Cards */
        .card {
          border: none;
          border-radius: var(--border-radius);
          box-shadow: var(--card-shadow);
          transition: var(--transition);
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
        }

        /* .card:hover {
  transform: translateY(-5px);
  box-shadow: var(--card-shadow-hover);
} */

        .card-header {
          background: var(--primary-gradient);
          color: white;
          border-radius: var(--border-radius) var(--border-radius) 0 0 !important;
          border: none;
          padding: 1.5rem;
        }

        .card-header h5 {
          margin: 0;
          font-weight: 600;
          font-size: 1.25rem;
        }

        .card-header p {
          margin: 0.5rem 0 0 0;
          opacity: 0.9;
          font-size: 0.95rem;
        }

        .card-body {
          padding: 2rem;
        }

        /* Enhanced Tabs */
        .nav-tabs {
          border: none;
          background: white;
          border-radius: var(--border-radius);
          padding: 0.5rem;
          box-shadow: var(--card-shadow);
          margin-bottom: 2rem;
        }

        .nav-tabs .nav-link {
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 500;
          color: #64748b;
          transition: var(--transition);
          margin: 0 0.25rem;
        }

        .nav-tabs .nav-link:hover {
          background: rgba(102, 126, 234, 0.1);
          color: var(--primary-color);
          transform: translateY(-2px);
        }

        .nav-tabs .nav-link.active {
          background: var(--primary-gradient);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
          transform: translateY(-2px);
        }

        /* Enhanced Language Selection Area */
        .language-selection-area {
          background: #2d3748;
          border-radius: var(--border-radius);
          padding: 1.5rem;
          margin-bottom: 2rem;
          color: white;
        }

        .selected-languages-container {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 10px;
          padding: 1.5rem;
          min-height: 100px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .language-badge {
          background: rgba(255, 255, 255, 0.9);
          color: var(--dark-color);
          border-radius: 25px;
          padding: 0.5rem 1rem;
          margin: 0.25rem;
          display: inline-flex;
          align-items: center;
          font-weight: 500;
          transition: var(--transition);
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .language-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }

        .language-badge .btn-close {
          /* background: none; */
          border: none;
          color: #e53e3e;
          font-size: 0.8rem;
          margin-left: 0.5rem;
          opacity: 0.7;
          transition: var(--transition);
        }

        .language-badge .btn-close:hover {
          opacity: 1;
          transform: scale(1.2);
        }

        /* Enhanced Search */
        .search-container {
          position: relative;
          margin-bottom: 2rem;
        }

        .search-input {
          border: 2px solid #e2e8f0;
          border-radius: 50px;
          padding: 1rem 1rem 1rem 3rem;
          font-size: 1rem;
          transition: var(--transition);
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
        }

        .search-input:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          outline: none;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.1rem;
        }

        /* Enhanced Language List */
        .languages-container {
          background: white;
          border-radius: var(--border-radius);
          box-shadow: var(--card-shadow);
          max-height: 500px;
          overflow-y: auto;
        }

        .language-item {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          transition: var(--transition);
          cursor: pointer;
        }

        .language-item:hover {
          background: linear-gradient(
            135deg,
            rgba(102, 126, 234, 0.05) 0%,
            rgba(118, 75, 162, 0.05) 100%
          );
          /* transform: translateX(5px); */
        }

        .language-item:last-child {
          border-bottom: none;
        }

        .language-checkbox {
          width: 1.2rem;
          height: 1.2rem;
          margin-right: 1rem;
          accent-color: var(--primary-color);
        }

        .language-flag {
          font-size: 1.5rem;
          margin-right: 1rem;
        }

        .language-info h6 {
          margin: 0;
          font-weight: 600;
          color: var(--dark-color);
        }

        .language-info small {
          color: #64748b;
          font-weight: 500;
        }

        /* Enhanced Buttons */
        .btn-enhanced {
          border-radius: 50px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: var(--transition);
          border: none;
          position: relative;
          overflow: hidden;
        }

        .btn-enhanced::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: var(--transition);
        }

        .btn-enhanced:hover::before {
          left: 100%;
        }

        .btn-primary-enhanced {
          background: var(--primary-gradient);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }

        .btn-primary-enhanced:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
        }

        .btn-success-enhanced {
          background: var(--success-gradient);
          color: white;
          box-shadow: 0 4px 15px rgba(72, 187, 120, 0.4);
        }

        .btn-success-enhanced:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(72, 187, 120, 0.6);
        }

        /* Enhanced Form Controls */
        .form-control-enhanced {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          transition: var(--transition);
          background: white;
        }

        .form-control-enhanced:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          outline: none;
        }

        .form-select-enhanced {
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          transition: var(--transition);
          background: white;
        }

        .form-select-enhanced:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          outline: none;
        }

        /* Enhanced Switches */
        .form-switch .form-check-input {
          width: 3rem;
          height: 1.5rem;
          background-color: #cbd5e0;
          border: none;
          transition: var(--transition);
        }

        .form-switch .form-check-input:checked {
          background: var(--success-gradient);
          box-shadow: 0 2px 10px rgba(72, 187, 120, 0.4);
        }

        .form-switch .form-check-input:focus {
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        /* Enhanced Settings Sections */
        .settings-section {
          background: white;
          border-radius: var(--border-radius);
          margin-bottom: 2rem;
          box-shadow: var(--card-shadow);
          transition: var(--transition);
        }

        .settings-section:hover {
          box-shadow: var(--card-shadow-hover);
        }

        .settings-section h6 {
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
        }

        /* Enhanced Feature Grid */
        .feature-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }

        .feature-item {
          background: rgba(102, 126, 234, 0.05);
          border-radius: 10px;
          padding: 1rem;
          transition: var(--transition);
        }

        .feature-item:hover {
          background: rgba(102, 126, 234, 0.1);
          transform: translateY(-2px);
        }

        /* Enhanced Alerts */
        .alert-enhanced {
          border: none;
          border-radius: var(--border-radius);
          padding: 1rem 1.5rem;
          margin-bottom: 2rem;
          box-shadow: var(--card-shadow);
        }

        .alert-success-enhanced {
          background: linear-gradient(
            135deg,
            rgba(72, 187, 120, 0.1) 0%,
            rgba(56, 161, 105, 0.1) 100%
          );
          color: #2f855a;
          border-left: 4px solid var(--success-color);
        }

        .alert-danger-enhanced {
          background: linear-gradient(
            135deg,
            rgba(245, 101, 101, 0.1) 0%,
            rgba(229, 62, 62, 0.1) 100%
          );
          color: #c53030;
          border-left: 4px solid var(--danger-color);
        }

        /* Loading Animation */
        .loading-spinner {
          display: inline-block;
          width: 1rem;
          height: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: spin 1s ease-in-out infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive Enhancements */
        @media (max-width: 768px) {
          .card-body {
            padding: 1.5rem;
          }

          .language-selection-area {
            padding: 1rem;
          }

          .selected-languages-container {
            padding: 1rem;
          }

          .feature-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Scrollbar Styling */
        .languages-container::-webkit-scrollbar {
          width: 6px;
        }

        .languages-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .languages-container::-webkit-scrollbar-thumb {
          background: var(--primary-color);
          border-radius: 3px;
        }

        .languages-container::-webkit-scrollbar-thumb:hover {
          background: #5a67d8;
        }

        /* Animation Classes */
        .fade-in {
          animation: fadeIn 0.5s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .slide-in {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default GlobalSettings;